// store/selectors/chatSelectors.ts
import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "../index.ts";
import type { Message, ChatHistoryEntry } from "../types/chatTypes";

// Base selectors
export const selectChatHistories = (state: RootState) => state.chat.chatHistories;
export const selectMessages = (state: RootState) => state.chat.messages;
export const selectMessagesByChatHistory = (state: RootState) => state.chat.messagesByChatHistory;
export const selectActiveChatHistoryId = (state: RootState) => state.chat.activeChatHistoryId;
export const selectChatLoading = (state: RootState) => state.chat.isLoading;
export const selectIsStreaming = (state: RootState) => state.chat.isStreaming;
export const selectChatError = (state: RootState) => state.chat.error;

/**
 * Parsed citation with source ID, display title, and URL
 */
export interface ParsedCitation {
    sourceId: string;
    displayTitle: string;
    url: string;
}

/**
 * Parse a citation string into its components
 *
 * Citation format: {sourceId}::{displayTitle}
 * Examples:
 *   d66ab825-fb84-4ce4-9aa6-f58d233c60ab::HHS Report.pdf
 *   a1b2c3d4-...-567890::https://fda.gov/ethics
 *
 * When a sourceUrls map is provided and contains an external URL for the
 * citation's sourceId, that URL is used instead of the internal API endpoint.
 * This allows citations to link directly to the original source (e.g. an FDA webpage).
 *
 * @param citation - The citation string from the LLM response
 * @param sourceUrls - Optional map of sourceId → external URL
 * @returns ParsedCitation with sourceId, displayTitle, and url
 */
/**
 * Format a source URL into a human-readable "domain — page" display name.
 * Returns null if the URL can't be parsed or has no useful path.
 */
function formatUrlDisplayName(sourceUrl: string): string | null {
    try {
        const url = new URL(sourceUrl);
        const domain = url.hostname.replace(/^www\./, "");
        const segments = url.pathname.split("/").filter(Boolean);
        const page = segments.length > 0 ? segments[segments.length - 1] : "";
        if (page) {
            const cleanPage = decodeURIComponent(page)
                .replace(/[-_]/g, " ")
                .replace(/\.\w+$/, "");
            return `${domain} — ${cleanPage}`;
        }
        return domain;
    } catch {
        return null;
    }
}

/**
 * Ensure a content URL has a trailing slash on its path when the path
 * doesn't already end with `/` and the last segment has no file
 * extension. Some hosts (notably petersonschriever.spaceforce.mil and
 * other DAF sites) return 404 on the non-slash form even though most
 * webservers redirect. Adding the slash is safe on servers that DON'T
 * require it — they either match the canonical form or 301 to it.
 *
 * Skips URLs with query strings, fragments, or file-extension endings
 * (`.pdf`, `.html`, etc.) since those explicitly identify a resource.
 */
function ensureTrailingSlash(url: string): string {
    if (!url) return url;
    if (url.startsWith("/api/")) return url; // our own API endpoints
    try {
        const parsed = new URL(url);
        // Leave query / fragment URLs alone — trailing-slash rules
        // usually apply to path-only URLs.
        if (parsed.search || parsed.hash) return url;
        const path = parsed.pathname;
        if (path.endsWith("/")) return url;
        // Last path segment has a `.` — probably a file (foo.html,
        // foo.pdf). Leave alone.
        const lastSeg = path.slice(path.lastIndexOf("/") + 1);
        if (lastSeg.includes(".")) return url;
        parsed.pathname = `${path}/`;
        return parsed.toString();
    } catch {
        return url;
    }
}

export const parseCitation = (citation: string, sourceUrls?: Record<string, string>): ParsedCitation => {
    const delimiterIndex = citation.indexOf("::");

    if (delimiterIndex === -1) {
        // Legacy format or plain filename - no source ID
        // Fall back to old document endpoint
        return {
            sourceId: "",
            displayTitle: citation,
            url: `/api/document/${encodeURIComponent(citation)}`
        };
    }

    const sourceId = citation.substring(0, delimiterIndex);
    const rawTitle = citation.substring(delimiterIndex + 2);

    // If we have an external source URL for this source, use it instead of the API endpoint
    // eslint-disable-next-line security/detect-object-injection -- Controlled property access with locally-derived sourceId key
    const externalUrl = sourceUrls?.[sourceId];
    const url = ensureTrailingSlash(
        externalUrl || `/api/source/${encodeURIComponent(sourceId)}/content`,
    );

    // Build a human-readable display title using the source URL when available
    let displayTitle = rawTitle;
    if (externalUrl) {
        const formatted = formatUrlDisplayName(externalUrl);
        if (formatted) {
            displayTitle = formatted;
        }
    }

    return {
        sourceId,
        displayTitle,
        url
    };
};

/**
 * Get the URL path for a citation
 * This is the simple helper for just getting the URL
 *
 * @param citation - The citation string
 * @param sourceUrls - Optional map of sourceId → external URL
 * @returns The URL path to fetch the document
 */
export const getCitationFilePath = (citation: string, sourceUrls?: Record<string, string>): string => {
    return parseCitation(citation, sourceUrls).url;
};

// Chat page selectors
export const selectAnswers = (state: RootState) => state.chat.answers;
export const selectStreamedAnswers = (state: RootState) => state.chat.streamedAnswers;
export const selectLastQuestion = (state: RootState) => state.chat.lastQuestion;
export const selectConversationLogId = (state: RootState) => state.chat.conversationLogId;

// Derived selector: has chat started (has a last question)
export const selectHasChatStarted = createSelector(selectLastQuestion, lastQuestion => lastQuestion.length > 0);

// Chat histories list sorted by most recent
export const selectChatHistoriesList = createSelector(selectChatHistories, (chatHistories): ChatHistoryEntry[] =>
    Object.values(chatHistories).sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
);

// Active chat history entry
export const selectActiveChatHistory = createSelector(selectChatHistories, selectActiveChatHistoryId, (chatHistories, activeId): ChatHistoryEntry | null =>
    // eslint-disable-next-line security/detect-object-injection -- Controlled property access with locally-derived key
    activeId ? (chatHistories[activeId] ?? null) : null
);

// Messages for active chat history
export const selectActiveChatHistoryMessages = createSelector(
    selectMessages,
    selectMessagesByChatHistory,
    selectActiveChatHistoryId,
    (messages, messagesByChatHistory, activeId): Message[] => {
        if (!activeId) return [];
        // eslint-disable-next-line security/detect-object-injection -- Controlled property access with locally-derived key
        const messageIds = messagesByChatHistory[activeId] || [];
        return messageIds
            // eslint-disable-next-line security/detect-object-injection -- Controlled property access with locally-derived key
            .map(id => messages[id])
            .filter(Boolean)
            .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }
);

// Messages by chat history ID (parameterized)
export const selectMessagesByChatHistoryId = createSelector(
    selectMessages,
    selectMessagesByChatHistory,
    (_state: RootState, conversationId: string) => conversationId,
    (messages, messagesByChatHistory, conversationId): Message[] => {
        // eslint-disable-next-line security/detect-object-injection -- Record lookup with typed entity ID
        const messageIds = messagesByChatHistory[conversationId] || [];
        return messageIds
            // eslint-disable-next-line security/detect-object-injection -- Controlled property access with locally-derived key
            .map(id => messages[id])
            .filter(Boolean)
            .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }
);

// Question/response pairs
export const selectMessagePairs = createSelector(selectActiveChatHistoryMessages, messages => {
    const pairs: { question: Message; response: Message | null }[] = [];

    for (let i = 0; i < messages.length; i++) {
        // eslint-disable-next-line security/detect-object-injection -- Controlled array index from loop/findIndex
        const message = messages[i];
        if (message.role === "user") {
            const response = messages[i + 1]?.role === "assistant" ? messages[i + 1] : null;
            pairs.push({ question: message, response });
        }
    }

    return pairs;
});

// Feedback statistics
export const selectFeedbackStats = createSelector(selectActiveChatHistoryMessages, messages => {
    const assistantMessages = messages.filter(m => m.role === "assistant");
    const withFeedback = assistantMessages.filter(m => m.feedback?.type);
    const positive = withFeedback.filter(m => m.feedback?.type === "positive").length;
    const negative = withFeedback.filter(m => m.feedback?.type === "negative").length;

    return {
        total: assistantMessages.length,
        withFeedback: withFeedback.length,
        positive,
        negative,
        positiveRate: withFeedback.length > 0 ? positive / withFeedback.length : 0
    };
});

// Single message by ID
// eslint-disable-next-line security/detect-object-injection -- Record lookup with typed entity ID
export const selectMessageById = (state: RootState, messageId: string): Message | undefined => state.chat.messages[messageId];

// Has messages check
export const selectChatHistoryHasMessages = createSelector(
    selectMessagesByChatHistory,
    selectActiveChatHistoryId,
    // eslint-disable-next-line security/detect-object-injection -- Controlled property access with locally-derived key
    (messagesByChatHistory, activeId): boolean => (activeId ? (messagesByChatHistory[activeId]?.length ?? 0) > 0 : false)
);
